/**
 * Authentication Context
 * Manages user authentication state and provides login/logout functions
 * Supports MFA (TOTP) verification flow
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import { API_GATEWAY_URL } from '@/api/client'

// Permission constants for dashboard access.
// Values follow the auth catalog's DOMAIN:ACTION / DOMAIN:SUBDOMAIN:ACTION convention
// (see C:\Projects\Mongo-backup\authdb.permissions.json) so they can be granted via the
// real permissions collection instead of relying on the removed isAdmin/hasFullAccess
// bypass. Where an existing catalog permission fits, it's reused (e.g. API_KEY:VIEW,
// ADMINISTRATION:USER_MANAGEMENT:VIEW, MONITORING:*, EVENT_INGESTION:VIEW, REDIS_CONSOLE:VIEW);
// where nothing in the catalog fits, a new self-referential permission is minted instead.
export const PERMISSIONS = {
  CAMPAIGN_MONITORING_DASHBOARD: 'CAMPAIGN_MONITORING_DASHBOARD:VIEW',
  FINANCE_MONITORING_DASHBOARD: 'MONITORING:FINANCE:VIEW',
  ADMIN: 'ADMIN',
  DEVELOPER: 'DEVELOPER',
  // Add more permissions as needed
  MONGO_CONSOLE_VIEW: 'MONGO_CONSOLE:VIEW',
  MONGO_CONSOLE_DELETE: 'MONGO_CONSOLE:DELETE',
  API_KEY_MANAGEMENT: 'API_KEY:VIEW',
  USER_MANAGEMENT: 'ADMINISTRATION:USER_MANAGEMENT:VIEW',
  DEVELOPER_CONSOLE: 'DEVELOPER_CONSOLE:VIEW',
  BATCH_JOB_MONITORING: 'MONITORING:EXECUTIONS:VIEW',
  DATA_INGESTION: 'EVENT_INGESTION:VIEW',
  ACCOUNT_MANAGEMENT: 'MONITORING:ACCOUNTS:VIEW',
  SYNC_MONITORING: 'SYNC_MONITORING:VIEW',
  PIPELINE_MONITORING: 'PIPELINE_MONITORING:VIEW',
  NON_UNIFIABLE_MONITORING: 'NON_UNIFIABLE_MONITORING:VIEW',
  DERIVATION_CONFIG: 'DERIVATION_CONFIG:VIEW',
  PRODUCT_DATA: 'PRODUCT_DATA:VIEW',
  REDIS_CONSOLE: 'REDIS_CONSOLE:VIEW',
  USAGE_ADJUSTMENTS_APPROVE: 'USAGE_ADJUSTMENTS:APPROVE',
} as const

// Role-to-permissions mapping for restricted roles
const ROLE_PERMISSIONS: Record<string, string[]> = {
  DATA_OPS: ['DEVELOPER_CONSOLE:VIEW', 'EVENT_INGESTION:VIEW', 'DERIVATION_CONFIG:VIEW'],
  CAMPAIGN_OPS: ['CAMPAIGN_MONITORING_DASHBOARD:VIEW'],
}

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

// User type based on the auth service response
interface User {
  id?: string
  username?: string
  email?: string
  accountId?: number
  roles?: string[]
  permissions?: string[]
}

// The login response's `user` object carries roles but no permissions. Decode the access
// token payload (no signature check — the API verifies the token; this is only for deciding
// what to show) and merge its roles, plus its legacy `permissions` claim where the auth
// service still emits one (auth.emit-legacy-permissions-claim).
function withTokenClaims(user: User, token: string): { user: User; hasPermissionsClaim: boolean } {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')))
    const hasPermissionsClaim = Array.isArray(claims.permissions)
    return {
      user: {
        ...user,
        roles: Array.isArray(claims.roles) ? claims.roles : user.roles,
        permissions: hasPermissionsClaim ? claims.permissions : user.permissions,
      },
      hasPermissionsClaim,
    }
  } catch {
    return { user, hasPermissionsClaim: false }
  }
}

// MFA challenge data from auth API
interface MfaChallenge {
  challengeId: string
  challengeType: 'totp' | 'sms' | 'email'
  expiresIn: number
  availableFactors: Array<{
    type: string
    displayName: string
    enabled: boolean
  }>
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  verifyMfa: (code: string) => Promise<void>
  cancelMfa: () => void
  logout: () => void
  error: string | null
  // MFA state
  mfaRequired: boolean
  mfaChallenge: MfaChallenge | null
  // Permission helpers
  hasPermission: (permission: Permission | string) => boolean
  hasAnyPermission: (permissions: (Permission | string)[]) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

// Auth API base URL - via API Gateway in production
// For local development, set VITE_AUTH_BASE_URL=http://localhost:8081
const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL || `${API_GATEWAY_URL}`

// Tokens without the legacy claim reference the permission set by a permSetHash claim
// instead; the auth service resolves the caller's own hash at /api/auth/me/permissions.
// If that call fails, keep whatever permissions the user already had (e.g. from the
// stored session) rather than blanking the menu.
async function resolveUser(user: User, token: string): Promise<User> {
  const { user: merged, hasPermissionsClaim } = withTokenClaims(user, token)
  if (hasPermissionsClaim) return merged
  try {
    const response = await fetch(`${AUTH_BASE_URL}/api/auth/me/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) {
      console.error('Failed to resolve permissions:', response.status)
      return merged
    }
    const data = await response.json()
    return Array.isArray(data.permissions) ? { ...merged, permissions: data.permissions } : merged
  } catch (err) {
    console.error('Failed to resolve permissions:', err)
    return merged
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null)
  const [pendingCredentials, setPendingCredentials] = useState<{ username: string; password: string } | null>(null)

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken')
    const storedUser = localStorage.getItem('user')

    if (!storedToken) {
      setIsLoading(false)
      return
    }
    setToken(storedToken)
    let parsedUser: User | null = null
    if (storedUser) {
      try {
        parsedUser = JSON.parse(storedUser)
      } catch {
        // Invalid user JSON, clear it
        localStorage.removeItem('user')
      }
    }
    if (!parsedUser) {
      setIsLoading(false)
      return
    }
    // Stay in the loading state until permissions resolve, so PermissionGuard doesn't
    // redirect away from the current page while they're still unknown
    resolveUser(parsedUser, storedToken).then(resolved => {
      localStorage.setItem('user', JSON.stringify(resolved))
      setUser(resolved)
      setIsLoading(false)
    })
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const loginUrl = `${AUTH_BASE_URL}/api/auth/login`
      console.log('🔍 DEBUG - AUTH_BASE_URL:', AUTH_BASE_URL)
      console.log('🔍 DEBUG - Login URL:', loginUrl)
      console.log('🔍 DEBUG - API_GATEWAY_URL:', API_GATEWAY_URL)

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          clientId: 'fh-web-client',
          grantType: 'password',
          deviceId: navigator.userAgent,
          userAgent: navigator.userAgent,
          ipAddress: 'client-ip',
        }),
      })

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const errorText = await response.text()
        console.error('Login failed:', response.status, errorText)
        throw new Error(`Authentication failed (${response.status}): ${errorText}`)
      }

      const data = await response.json()

      // Handle various response scenarios
      if (data.status === 'invalid_credentials' || data.status === 'error') {
        throw new Error(data.message || 'Invalid username or password')
      }

      if (data.status === 'mfa_required') {
        // Store MFA challenge and credentials for verification
        setMfaChallenge({
          challengeId: data.challengeId,
          challengeType: data.challengeType,
          expiresIn: data.expiresIn,
          availableFactors: data.availableFactors || [],
        })
        setPendingCredentials({ username, password })
        setMfaRequired(true)
        setIsLoading(false)
        return // Don't throw - MFA flow will continue
      }

      if (data.status === 'mfa_enrollment_required') {
        throw new Error('MFA enrollment is required. Please complete setup via the main FirstHive portal.')
      }

      if (data.accessToken) {
        // Store token and user
        localStorage.setItem('accessToken', data.accessToken)
        setToken(data.accessToken)

        if (data.user) {
          const user = await resolveUser(data.user, data.accessToken)
          localStorage.setItem('user', JSON.stringify(user))
          setUser(user)
        }

        if (data.username) {
          localStorage.setItem('username', data.username)
        }
      } else {
        throw new Error(data.message || 'Login failed. Please try again.')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred during login'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const verifyMfa = useCallback(async (code: string) => {
    if (!mfaChallenge || !pendingCredentials) {
      throw new Error('No MFA challenge pending')
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          challengeId: mfaChallenge.challengeId,
          code,
          factorType: mfaChallenge.challengeType, // 'totp', 'sms', or 'email'
          username: pendingCredentials.username,
          clientId: 'fh-admin-portal',
          deviceId: navigator.userAgent,
          userAgent: navigator.userAgent,
        }),
      })

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const errorText = await response.text()
        console.error('MFA verification failed:', response.status, errorText)
        throw new Error(`MFA verification failed (${response.status}). Please try again.`)
      }

      const data = await response.json()

      if (data.status === 'error' || data.status === 'invalid_code') {
        throw new Error(data.message || 'Invalid verification code')
      }

      if (data.accessToken) {
        // MFA verified successfully - store token and user
        localStorage.setItem('accessToken', data.accessToken)
        setToken(data.accessToken)

        if (data.user) {
          const user = await resolveUser(data.user, data.accessToken)
          localStorage.setItem('user', JSON.stringify(user))
          setUser(user)
        }

        if (data.username) {
          localStorage.setItem('username', data.username)
        }

        // Clear MFA state
        setMfaRequired(false)
        setMfaChallenge(null)
        setPendingCredentials(null)
      } else {
        throw new Error(data.message || 'MFA verification failed. Please try again.')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred during MFA verification'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [mfaChallenge, pendingCredentials])

  const cancelMfa = useCallback(() => {
    setMfaRequired(false)
    setMfaChallenge(null)
    setPendingCredentials(null)
    setError(null)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('user')
    localStorage.removeItem('username')
    setToken(null)
    setUser(null)
    setError(null)
    setMfaRequired(false)
    setMfaChallenge(null)
    setPendingCredentials(null)
  }, [])

  // Check if user has a specific permission.
  // No ADMIN/DEVELOPER full-access bypass: those roles must be granted permissions
  // explicitly (via the roles collection), same as every other role.
  const hasPermission = useCallback((permission: Permission | string): boolean => {
    if (!user) return false
    // Check user permissions array
    if (user.permissions?.some(perm => perm.toUpperCase() === permission.toUpperCase())) {
      return true
    }
    // Check roles as fallback (some systems use role-based permissions)
    if (user.roles?.some(role => role.toUpperCase() === permission.toUpperCase())) {
      return true
    }
    // Check role-to-permissions mapping via roles array (e.g. DATA_OPS grants specific permissions)
    if (user.roles?.some(role => ROLE_PERMISSIONS[role.toUpperCase()]?.includes(permission.toUpperCase()))) {
      return true
    }
    // Check role-to-permissions mapping via permissions array (backend may return roles inside permissions)
    if (user.permissions?.some(perm => ROLE_PERMISSIONS[perm.toUpperCase()]?.includes(permission.toUpperCase()))) {
      return true
    }
    return false
  }, [user])

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissions: (Permission | string)[]): boolean => {
    return permissions.some(perm => hasPermission(perm))
  }, [hasPermission])

  const value: AuthContextType = {
    isAuthenticated: !!token,
    isLoading,
    user,
    token,
    login,
    verifyMfa,
    cancelMfa,
    logout,
    error,
    mfaRequired,
    mfaChallenge,
    // Permission helpers
    hasPermission,
    hasAnyPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
